import React, { useEffect, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  Chip,
  Box,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import {
  AUDIT_CALLING_DETAILS_REPORT,
  OUTGOING_CALL_REPORT,
} from "../../constants";
import CallIcon from "@mui/icons-material/Call";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

function ConnectedCallTable({
  filters,
  setFilters,
  setMetricData,
  setLoading,
}) {
  const empDetails = JSON.parse(localStorage.getItem("userData"));

  const navigate = useNavigate();

  const location = useLocation();
  const { prevFilters } = location.state || {}; // Access prevFilters from state
  const [page, setPage] = useState(0); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
  const [totalCount, setTotalCount] = useState(0);
  const [openRows, setOpenRows] = useState([]); // To manage which rows are expanded

  const { agentName, agentNo } = useParams();

  const [callData, setCallData] = useState([]);

  const isInitialLoad = useRef(true);

  const fetchConnectedData = () => {
    const { startDate, endDate } = filters;

    // Determine yesterday's date
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");

    // Format startDate and endDate, defaulting to yesterday if not provided
    const formattedStartDate = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : yesterday;
    const formattedEndDate = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : yesterday;

    // Build query params
    const queryParams = new URLSearchParams();
    queryParams.append("startDate", formattedStartDate);
    queryParams.append("endDate", formattedEndDate);
    if (localStorage.getItem("empRole") === "100") {
      queryParams.append("aPartyNo", empDetails.EmployeePhone);
    } else {
      queryParams.append("aPartyNo", agentNo); 
    }
    queryParams.append("callStatus", "Connected");
    queryParams.append("page", page + 1); // Current page (1-based index)
    queryParams.append("pageSize", rowsPerPage); // Number of rows per page
    setLoading(true);
    axios
      .get(`${OUTGOING_CALL_REPORT}?${queryParams.toString()}`)
      .then((response) => {
        const { connected, pagination, summary } = response.data;

        setCallData(connected.calls); // Update the call data
        setTotalCount(pagination.totalRecords); // Update the total record count
        setMetricData(summary);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching connected data:", error);
        setCallData([]); // Set call data to empty on error
        setLoading(false);
      });
  };

  // Initialize filters with prevFilters
  useEffect(() => {
    if (localStorage.getItem("empRole") === "101") {
      if (prevFilters) {
        console.log("Applying prevFilters:", prevFilters);
        setFilters(prevFilters);
      } else {
        // Default to the last 7 days
        setFilters({
          startDate: dayjs().subtract(7, "days").format("YYYY-MM-DD"),
          endDate: dayjs().format("YYYY-MM-DD"),
        });
      }
      isInitialLoad.current = false; // Mark initial load as complete
    }
  }, [prevFilters]);

  // Fetch data when filters, page, or rowsPerPage change
  useEffect(() => {
    if (localStorage.getItem("empRole") === "101") {
      if (isInitialLoad.current || !filters.startDate || !filters.endDate) {
        return; // Skip if it's the initial load or filters are incomplete
      }
      console.log("Fetching data with filters:", filters);
      fetchConnectedData();
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    if (localStorage.getItem("empRole") === "100") {
      fetchConnectedData();
    }
  }, [filters, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  // Handle row expansion toggle
  const handleRowToggle = (index) => {
    setOpenRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((rowIndex) => rowIndex !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const formatDate = (dateString) =>
    moment(dateString).format("DD-MM-YYYY HH:mm:ss");

  const IconCell = ({ row }) => (
    <>
    <Tooltip title="Call" arrow>
      <IconButton
        style={{
          border: "1px solid",
          borderRadius: "4px",
          padding: "2px",
          width: "24px",
          height: "24px",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: '#fff'
        }}
        color="primary"
        onClick={() => handleCallIconClick(row)}
      >
        <CallIcon fontSize="small" color="primary" />
      </IconButton>
      </Tooltip>
    </>
  );

  const handleCallIconClick = (row) => {
    navigate("/dashboard", {
      state: {
        mobNo: row.customerNumber,
        lotNum: row.customerNumber,
      },
    });
  };

  const handlePlayRecording = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      alert("No recording URL available.");
    }
  };

  return (
    <>
      <Paper>
        <TableContainer component={Paper} elevation={3}>
          <Table
            sx={{
              minWidth: 650,
              height: "100%",
              tableLayout: "fixed",
            }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#EEF0F4" }}>
                <TableCell width={50} sx={{ textAlign: "center" }} />
                <TableCell
                  width={50}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  SNo
                </TableCell>

                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Mobile Number
                </TableCell>
                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Customer Name
                </TableCell>
                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Lot Number
                </TableCell>
                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Zone Name
                </TableCell>
                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Branch Name
                </TableCell>
                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Status
                </TableCell>
                <TableCell
                  width={120}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Duration (Minutes)
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    width: "80px",
                    position: "sticky", // Make the cell sticky
                    right: 0, // Fix to the right side
                    backgroundColor: "#EEF0F4", // Set background color for visibility
                    zIndex: 100, // Higher z-index to stay above other cells
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    top: 0,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(callData) && callData.length > 0 ? (
                callData.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <TableRow
                      sx={{
                        "&:hover": { backgroundColor: "#f9f9f9" },
                      }}
                    >
                      <TableCell>
                        <IconButton
                          onClick={() => handleRowToggle(rowIndex)}
                          sx={{ padding: 0 }}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell width={50} sx={{ textAlign: "center" }}>
                        {page * rowsPerPage + rowIndex + 1}
                      </TableCell>
                      {/* <TableCell width={160} sx={{ textAlign: "center" }}>
                          {row.customer_details === null
                            ? "New Customer"
                            : row.customer_details?.farmer_name}
                        </TableCell> */}
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.customerNumber || "-"}
                      </TableCell>
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.customerDetails?.farmerName || "-"}
                      </TableCell>
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.customerDetails?.lotNumber || "-"}
                      </TableCell>

                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.customerDetails?.zoneName || "-"}
                      </TableCell>
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.customerDetails?.branchName || "-"}
                      </TableCell>
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        <Chip
                          label={
                            row.customerDetails?.status === "open"
                              ? "Open"
                              : "Closed"
                          }
                          color={
                            row.customerDetails?.status === "open"
                              ? "error"
                              : "success"
                          }
                          size="small"
                          sx={{ textTransform: "capitalize", margin: "5px" }}
                        />
                      </TableCell>

                      <TableCell
                        width={120}
                        sx={{
                          textAlign: "center",
                          textTransform: "capitalize",
                        }}
                      >
                        {row.callDuration || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "80px",
                          padding: 1,
                          position: "sticky", // Make the cell sticky
                          right: 0, // Fix to the right side
                          backgroundColor: "#EEF0F4", // Set background color for visibility
                          zIndex: 10, // Higher z-index to stay above other cells
                          boxShadow: "0 2px 5px rgba(0,0,0,0.1)", // Add a shadow to the column
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex", // Flex layout for icon buttons
                            gap: "4px", // Add gap between buttons
                            justifyContent: "flex-start", // Center align buttons horizontally
                            alignItems: "flex-start", // Center align buttons vertically
                            padding: "4px", // Optional padding around the group
                          }}
                        >
                          {row.customerDetails?.status === "open" && (
                            <IconCell row={row} />
                          )}

                          {row.recordingUrl ? (
                            <Tooltip title="Recording" arrow>
                            <IconButton
                              sx={{
                                border: "1px solid",
                                borderRadius: "4px",
                                padding: "2px",
                                width: "24px",
                                height: "24px",
                                justifyContent: "center",
                                alignItems: "center",
                                transition: "color 0.3s",
                                "&:hover": { color: "#0B9B73" },
                                backgroundColor: "#fff"
                              }}
                              color="primary"
                              onClick={() => handlePlayRecording(row.recordingUrl)}
                            >
                              <PlayArrowIcon />
                            </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              No Recording
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={openRows.includes(rowIndex)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Paper sx={{ padding: 2 }}>
                            <Typography variant="h6">
                              Customer Details
                            </Typography>
                            <Typography>
                              <strong>Connected On:</strong> {row.callStartDate}
                            </Typography>
                            <Typography>
                              <strong>Connected At:</strong>{" "}
                              {row.callStartTime === null
                                ? "-"
                                : row.callStartTime}
                            </Typography>

                            <Typography>
                              <strong>Shed Type:</strong>{" "}
                              {row.customerDetails?.shedType || "-"}
                            </Typography>

                            <Typography>
                              <strong>Vendor:</strong>{" "}
                              {row.customerDetails?.vendor || "-"}
                            </Typography>
                          </Paper>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="left">
                    No details available yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]} // Options for rows per page
          component="div"
          count={totalCount} // Total number of rows
          rowsPerPage={rowsPerPage} // Current rows per page
          page={page} // Current page
          onPageChange={handleChangePage} // Handle page change
          onRowsPerPageChange={handleChangeRowsPerPage} // Handle rows per page change
        />
      </Paper>
    </>
  );
}

export default ConnectedCallTable;
